// components/CTAWithFAQ.tsx

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ArrowRight,
  Sparkles,
  MessageCircle,
  Mail,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

interface FAQ {
  id: string;
  question: string;
  answer: string;
}
const APP_URL = process.env.REACT_APP_DASHBOARD_URL;

const faqs: FAQ[] = [
  {
    id: "1",
    question: "How do I get started with Unimerg?",
    answer:
      "Getting started is simple! Create your free account, complete your profile with your skills and expertise, then browse available projects. Once approved, you can start contributing immediately and earn based on the tasks you complete.",
  },
  {
    id: "2",
    question: "What qualifications do I need?",
    answer:
      "We welcome contributors from diverse backgrounds. While specific projects may require domain expertise (e.g., medicine, law, linguistics), many tasks only require strong attention to detail, critical thinking, and proficiency in your native language.",
  },
  {
    id: "3",
    question: "How and when do I get paid?",
    answer:
      "Payments are processed weekly via your preferred method (PayPal, bank transfer, or Wise). You can track your earnings in real-time through your dashboard. Most contributors receive payment within 3-5 business days after the payment cycle.",
  },
  {
    id: "4",
    question: "Can I work from anywhere?",
    answer:
      "Yes! Unimerg is fully remote. You can work from anywhere in the world with a stable internet connection. We have contributors in over 150 countries working on their own schedules.",
  },
  {
    id: "5",
    question: "How much can I earn?",
    answer:
      "Earnings vary based on your expertise, project complexity, and time invested. Our contributors earn between $15-100+ per hour depending on the task type. Top performers in specialized fields can earn significantly more.",
  },
];

const CTAWithFAQ: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<string | null>("1");

  const toggleFAQ = (id: string) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section className="relative py-16 bg-gradient-to-b from-white to-[#FAFAFA] overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#8B52FF]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#E05A6A]/5 rounded-full blur-[120px]" />

      {/* Subtle pattern */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(#8B52FF 1px, transparent 1px)`,
          backgroundSize: "30px 30px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Side - CTA */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-32"
          >
            <div className="relative">
              {/* Main CTA Card */}
              <div className="relative rounded-3xl overflow-hidden">
                {/* Gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#8B52FF] to-[#E05A6A]" />

                {/* Pattern overlay */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                    backgroundSize: "24px 24px",
                  }}
                />

                {/* Decorative shapes */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

                {/* Content */}
                <div className="relative z-10 p-8 lg:p-12">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm mb-6">
                    <Sparkles className="w-4 h-4 text-white" />
                    <span className="text-sm font-medium text-white">
                      Join 50,000+ Contributors
                    </span>
                  </div>

                  <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
                    Ready to Shape
                    <br />
                    the Future of AI?
                  </h2>

                  <p className="text-white/80 text-lg mb-8 max-w-md">
                    Start earning today while contributing to groundbreaking AI
                    development. Flexible hours, competitive pay, global impact.
                  </p>

                  {/* Benefits list */}
                  <div className="space-y-3 mb-8">
                    {[
                      "Work from anywhere, anytime",
                      "Weekly payments, no minimums",
                      "Access to exclusive projects",
                    ].map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="flex items-center gap-3"
                      >
                        <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-white/90">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link to={`${APP_URL}/signup`}>
                      <motion.button
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full sm:w-auto px-8 py-4 bg-white text-[#8B52FF] rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-2 group"
                      >
                        Get Started
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </motion.button>
                    </Link>
                    <Link to="/careers">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full sm:w-auto px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all duration-300"
                      >
                        View Opportunities
                      </motion.button>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Contact card below */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="mt-6 p-6 rounded-2xl bg-white border border-gray-100 shadow-sm"
              >
                <div className="flex items-start md:items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-[#8B52FF]" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">
                      Still have questions?
                    </h4>
                    <p className="text-sm text-gray-500">
                      Our team is here to help
                    </p>
                    <Link to="/contact">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex md:hidden mt-3 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors items-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        Contact Us
                      </motion.button>
                    </Link>
                  </div>
                  <Link to="/contact">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors hidden md:flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      Contact Us
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side - FAQ */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-8">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-sm font-medium text-[#8B52FF] mb-4"
              >
                FAQ
              </motion.span>
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                Frequently Asked{" "}
                <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
                  Questions
                </span>
              </h3>
            </div>

            {/* FAQ Accordion */}
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div
                    className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                      openFAQ === faq.id
                        ? "bg-gradient-to-br from-[#8B52FF]/5 to-[#E05A6A]/5 border-[#8B52FF]/20 shadow-lg shadow-[#8B52FF]/5"
                        : "bg-white border-gray-100 hover:border-[#8B52FF]/20"
                    }`}
                  >
                    <button
                      onClick={() => toggleFAQ(faq.id)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left"
                    >
                      <span
                        className={`font-semibold pr-4 transition-colors ${
                          openFAQ === faq.id
                            ? "text-[#8B52FF]"
                            : "text-gray-900"
                        }`}
                      >
                        {faq.question}
                      </span>
                      <motion.div
                        animate={{ rotate: openFAQ === faq.id ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          openFAQ === faq.id
                            ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {openFAQ === faq.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <div className="px-6 pb-5">
                            <div className="h-px bg-gradient-to-r from-[#8B52FF]/20 to-[#E05A6A]/20 mb-4" />
                            <p className="text-gray-600 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Additional help link */}
            {/* <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="mt-8 text-center lg:text-left"
            >
              <p className="text-gray-600">
                Can't find what you're looking for?{" "}
                <Link
                  to="/contact"
                  className="text-[#8B52FF] font-medium hover:underline inline-flex items-center gap-1"
                >
                  Get in touch
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </p>
            </motion.div> */}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CTAWithFAQ;
